import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FormFieldError } from '@/components/ui/form-field-error';
import { Label } from '@/components/ui/label';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { getTipDefinition, type TipId } from '@/tips';
import { cn } from '@/utils/cn';

import { Tip } from './tip';

/**
 * Label, control, hint and error, wired together.
 *
 * The wiring is the point: `htmlFor`, `aria-describedby` and `aria-invalid` are what
 * make a form usable with a screen reader, and they are exactly the three things that
 * get forgotten when every form assembles its own fields.
 */

export interface FormFieldProps {
  label: string;
  /** Receives the ids to spread onto the control. */
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    invalid: boolean;
  }) => ReactNode;
  /** Guidance shown before the user makes a mistake, not after. Prefer `tipId`. */
  hint?: string;
  /** Catalog tip — inline mode fills the hint slot; icon mode sits beside the label. */
  tipId?: TipId;
  popoverPortal?: PopoverPortalTarget;
  error?: string;
  required?: boolean;
  requiredLabel?: string;
  optionalLabel?: string;
  className?: string;
}

export const FormField = ({
  label,
  children,
  hint,
  tipId,
  popoverPortal,
  error,
  required = false,
  requiredLabel,
  optionalLabel,
  className,
}: FormFieldProps) => {
  const id = useId();
  const { t } = useTranslation('tips');
  const tipMode = tipId ? getTipDefinition(tipId).mode : null;
  const tipContent = tipId ? t(tipId) : undefined;

  const showIconTip = tipMode === 'icon';
  const captionFromTip =
    tipMode === 'inline' || tipMode === 'label' ? tipContent : undefined;
  const captionText = captionFromTip ?? (!tipId ? hint : undefined);

  const hintId = captionText ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Label htmlFor={id} required={required} requiredLabel={requiredLabel}>
            {label}
          </Label>
          {showIconTip && tipId ? (
            <Tip tipId={tipId} mode="icon" popoverPortal={popoverPortal} />
          ) : null}
        </div>
        {!required && optionalLabel ? (
          <span className="text-caption text-fg-tertiary">{optionalLabel}</span>
        ) : null}
      </div>

      {children({
        id,
        'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
        invalid: Boolean(error),
      })}

      {captionText && !error ? (
        tipId && (tipMode === 'inline' || tipMode === 'label') ? (
          <Tip tipId={tipId} mode="inline" id={hintId} />
        ) : (
          <p id={hintId} className="text-caption text-fg-secondary">
            {captionText}
          </p>
        )
      ) : null}

      <FormFieldError id={errorId} message={error} />
    </div>
  );
};
